<template>
  <el-row class="d-flex">
    <el-button
      class="ml-auto"
      type="primary"
      @click="showNewConfigurationModal = true"
    >
      <h4>{{ $t('new') }}</h4>
    </el-button>
  </el-row>
  <el-row
    v-for="(item, index) in localConfigurations"
    :key="index"
    class="d-block mt-1"
  >
    <broker-sftp-configuration-card
      :configuration="item"
      :configuration-history="brokerSftpConfigurationHistory"
      @update="(payload: BrokerSftpConfiguration) => handleConfigurationUpdate(index, payload)"
      @delete="handleConfigurationDelete(index)"
      @save="handleConfigurationSave(index)"
      @fetch-history="fetchBrokerSftpConfigurationHistory(item.brokerId ?? '')"
    />
  </el-row>
  <new-broker-sftp-configuration-modal
    :model-value="showNewConfigurationModal"
    :existing-names="localConfigurations.map(el => el.brokerId ?? '')"
    @close="showNewConfigurationModal = false"
    @confirm="handleConfigurationCreate"
  />
  <base-confirmation-modal
    v-model="showDeleteConfigurationModal"
    :feedback-text="$t('configurationDeleted')"
    @confirm="deleteConfiguration"
    @close="
      {
        brokerIdToBeDeleted = null;
        showDeleteConfigurationModal = false;
      }
    "
  >
    <template #content>
      <p>{{ $t('confirmDeleteConfiguration') }}</p>
      <p class="mt-2">
        <strong>{{ $t('confirmDeleteConfigurationNote') }}</strong>
      </p>
    </template>
  </base-confirmation-modal>
</template>
<script lang="ts">
import { ElButton, ElMessage, ElRow } from 'element-plus';
import NewBrokerSftpConfigurationModal from './components/NewBrokerSftpConfigurationModal.vue';
import BrokerSftpConfigurationCard from '@/views/admin/engagements/components/BrokerSftpConfigurationCard.vue';
import { computed, ComputedRef, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import i18n from '@/i18n';
import { BrokerSftpConfiguration } from '@etp/etp-gain-bff-client/axios';
import BaseConfirmationModal from '@/components/modals/BaseConfirmationModal.vue';

export default {
  name: 'EngagementBrokerSftpConfiguration',
  components: {
    ElButton,
    ElRow,
    BaseConfirmationModal,
    NewBrokerSftpConfigurationModal,
    BrokerSftpConfigurationCard
  },
  setup() {
    let engagementId = '';
    const store = useStore();
    const router = useRouter();

    const localConfigurations = ref(new Array<BrokerSftpConfiguration>());
    const showNewConfigurationModal = ref(false);
    const showDeleteConfigurationModal = ref(false);
    const brokerIdToBeDeleted = ref<string | undefined | null>(null);

    const brokerSftpConfigurationHistory: ComputedRef<
      Array<BrokerSftpConfiguration>
    > = computed(
      () => store.getters['engagements/getBrokerSftpConfigurationHistory']
    );

    const handleConfigurationCreate = async (name: string) => {
      localConfigurations.value.push({
        brokerId: name
      } as BrokerSftpConfiguration);
      showNewConfigurationModal.value = false;
    };

    const handleConfigurationDelete = async (index: number) => {
      const configuration = localConfigurations.value[index];
      if (configuration.version === undefined) {
        localConfigurations.value.splice(index, 1);
      } else {
        showDeleteConfigurationModal.value = true;
        brokerIdToBeDeleted.value = configuration.brokerId;
      }
    };

    const deleteConfiguration = async () => {
      await store.dispatch('engagements/deleteBrokerSftpConfiguration', {
        engagementId,
        brokerId: brokerIdToBeDeleted.value
      });
      brokerIdToBeDeleted.value = null;
      showDeleteConfigurationModal.value = false;
      await fetchConfigurations();
    };

    const handleConfigurationUpdate = async (
      index: number,
      payload: BrokerSftpConfiguration
    ) => {
      localConfigurations.value[index].enabled = payload.enabled;
      localConfigurations.value[index].hostName = payload.hostName;
      localConfigurations.value[index].userName = payload.userName;
      localConfigurations.value[index].passwordKeyName =
        payload.passwordKeyName;
      localConfigurations.value[index].sshKeyKeyName = payload.sshKeyKeyName;
      localConfigurations.value[index].port = payload.port;
      localConfigurations.value[index].scanPath = payload.scanPath;
      localConfigurations.value[index].fileFilters = payload.fileFilters;
    };

    const handleConfigurationSave = async (index: number) => {
      const statusCode = await store.dispatch(
        'engagements/createOrUpdateBrokerSftpConfiguration',
        {
          engagementId,
          configuration: localConfigurations.value[index]
        }
      );
      ElMessage({
        message: i18n.global.t(
          statusCode > 300
            ? 'brokerSftpConfigurationSaveFailure'
            : 'brokerSftpConfigurationSaveSuccess'
        )
      });
      await fetchConfigurations();
    };

    const fetchConfigurations = async () => {
      await store.dispatch(
        'engagements/fetchBrokerSftpConfigurations',
        engagementId
      );
      localConfigurations.value = [
        ...store.getters['engagements/getBrokerSftpConfigurations']
      ];
    };

    const fetchBrokerSftpConfigurationHistory = async (brokerId: string) => {
      await store.dispatch('engagements/fetchBrokerSftpConfigurationHistory', {
        engagementId,
        brokerId
      });
    };

    onMounted(async () => {
      engagementId = router.currentRoute.value.params.engagementId as string;
      await fetchConfigurations();
    });

    return {
      handleConfigurationCreate,
      handleConfigurationDelete,
      handleConfigurationUpdate,
      handleConfigurationSave,
      localConfigurations,
      showNewConfigurationModal,
      fetchBrokerSftpConfigurationHistory,
      brokerSftpConfigurationHistory,
      showDeleteConfigurationModal,
      deleteConfiguration,
      brokerIdToBeDeleted
    };
  }
};
</script>
